export type IdentitySource = Record<string, unknown> | null | undefined;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function looksLikeEmail(value: unknown): boolean {
  const text = cleanText(value);
  return EMAIL_PATTERN.test(text);
}

export function maskEmailAddress(value: unknown): string {
  const email = cleanText(value);
  if (!looksLikeEmail(email)) return email || '—';

  const [localPart, domain = ''] = email.split('@');
  const [domainName = '', ...domainSuffixParts] = domain.split('.');
  const domainSuffix = domainSuffixParts.length > 0 ? `.${domainSuffixParts.join('.')}` : '';

  const maskedLocal = localPart.length <= 2
    ? `${localPart.charAt(0) || '*'}***`
    : `${localPart.slice(0, 2)}***`;
  const maskedDomain = domainName.length <= 1
    ? `${domainName || '*'}***`
    : `${domainName.slice(0, 1)}***`;

  return `${maskedLocal}@${maskedDomain}${domainSuffix}`;
}

function firstNameFromFullName(value: unknown): string {
  const text = cleanText(value).replace(/\s+/g, ' ');
  if (!text || looksLikeEmail(text)) return '';
  return text.split(' ')[0] || '';
}

function firstAvailableName(source: IdentitySource, keys: string[]): string {
  if (!source) return '';
  for (const key of keys) {
    const value = firstNameFromFullName(source[key]);
    if (value) return value;
  }
  return '';
}

export function displayNameForIdentity(source: IdentitySource, fallback = 'User'): string {
  const firstName = firstAvailableName(source, [
    'givenName',
    'given_name',
    'firstName',
    'first_name',
  ]);
  if (firstName) return firstName;

  const displayName = firstAvailableName(source, [
    'displayName',
    'display_name',
    'contactName',
    'contact_name',
    'fullName',
    'full_name',
    'name',
    'preferred_username',
    'username',
  ]);
  if (displayName) return displayName;

  return fallback;
}

export function notificationContactSummary(value: unknown): string {
  const emails = Array.isArray(value)
    ? value.map((entry) => cleanText(entry)).filter(Boolean)
    : cleanText(value)
      .split(/[;,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  if (emails.length === 0) return '—';
  if (emails.length === 1) return '1 configured contact';
  return `${emails.length} configured contacts`;
}

export function redactEmailsDeep<T>(value: T): T {
  if (typeof value === 'string') {
    return (looksLikeEmail(value) ? maskEmailAddress(value) : value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => redactEmailsDeep(entry)) as T;
  }
  if (value && typeof value === 'object') {
    const redacted: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('email')) {
        if (Array.isArray(entry)) {
          redacted[key] = entry.map((item) => maskEmailAddress(item));
        } else {
          redacted[key] = maskEmailAddress(entry);
        }
      } else {
        redacted[key] = redactEmailsDeep(entry);
      }
    }
    return redacted as T;
  }
  return value;
}
